import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { titles } from '../recs/engine';
import { useAnalytics } from '../recs/analytics';
import './SearchBar.css';

const MAX_SUGGESTIONS = 6;

export default function SearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [confirmedText, setConfirmedText] = useState<string | null>(null);
  const { logSearch } = useAnalytics();
  const confirmTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return titles
      .filter(
        (t) =>
          t.title_name.toLowerCase().includes(q) ||
          t.genre_primary.toLowerCase().includes(q) ||
          t.language.toLowerCase().includes(q)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [query]);

  const confirm = (text: string) => {
    setConfirmedText(text);
    if (confirmTimeout.current) clearTimeout(confirmTimeout.current);
    confirmTimeout.current = setTimeout(() => setConfirmedText(null), 2000);
  };

  const submitQuery = () => {
    const text = query.trim();
    if (!text) return;
    const top = matches[0];
    logSearch({
      queryText: text,
      resultCount: matches.length,
      resultTitleId: top?.title_id ?? null,
      resultTitleName: top?.title_name ?? null,
    });
    confirm(text);
    setOpen(false);
    if (top) {
      setQuery('');
      navigate(`/title/${top.title_id}`);
    }
  };

  const selectSuggestion = (titleId: string, titleName: string) => {
    const text = query.trim();
    logSearch({ queryText: text, resultCount: matches.length, resultTitleId: titleId, resultTitleName: titleName });
    confirm(text);
    setQuery('');
    setOpen(false);
    navigate(`/title/${titleId}`);
  };

  return (
    <div className="search-bar">
      <input
        className="search-bar__input"
        type="search"
        placeholder="Search titles, genres, languages…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitQuery();
        }}
        aria-label="Search"
      />
      {open && query.trim() && (
        <ul className="search-bar__suggestions">
          {matches.length === 0 ? (
            <li className="search-bar__empty">No matches — press Enter to log this search anyway</li>
          ) : (
            matches.map((t) => (
              <li key={t.title_id}>
                <button type="button" onMouseDown={() => selectSuggestion(t.title_id, t.title_name)}>
                  <span className="search-bar__suggestion-name">{t.title_name}</span>
                  <span className="search-bar__suggestion-meta">
                    {t.language} · {t.genre_primary}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {confirmedText && <span className="search-bar__confirm">Captured "{confirmedText}"</span>}
    </div>
  );
}
